import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table, Button, Space, Card, Modal, Form, Input, Select, Tag, Popconfirm, message,
  Tooltip, Row, Col, InputNumber, Upload, Badge, Divider, Tabs, Alert, Empty, Typography
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  DownloadOutlined, UploadOutlined, SaveOutlined,
  FileExcelOutlined, SearchOutlined, CheckCircleOutlined,
  BookOutlined, SettingOutlined, UserAddOutlined, CloudUploadOutlined,
  TableOutlined, FilterOutlined, ReloadOutlined, ClusterOutlined
} from '@ant-design/icons';

import {
  fetchCandidateSubjectConfigs,
  saveCandidateSubjectConfigs,
  fetchCandidateResultConfig,
  saveCandidateResultConfig,
  recalculateCandidateResults,
  fetchCandidates,
  createCandidate,
  updateCandidate,
  deleteCandidate,
  bulkUploadCandidates,
  bulkDeleteCandidates
} from '../../../../Service/Candidates_API/candidatesApi';

import {
  fetchAcademicYears,
  fetchDashboardFilterOptions,
  fetchDashboardAnalytics
} from '../../../../Service/Dashboard_API/dashboardApi';
import { fetchClusters, fetchClusterInstitutions } from '../../../../Service/Clusters_API/clustersApi';
import { fetchStandards } from '../../../../Service/Syllabus_API/syllabusApi';
import { ClusterInstitutionAnalyticsCard } from '../../DashboardPages/DashboardComponents/AnalyticsCharts';

const { Option } = Select;
const { Text } = Typography;

// Simple CSV Parser Helper
const parseCSVText = (text) => {
  const lines = text.split(/\r\n|\n/);
  const result = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    
    // Parse CSV row respecting quotes
    const row = [];
    let insideQuote = false;
    let entry = '';
    for (let c = 0; c < line.length; c++) {
      const char = line[c];
      if (char === '"') {
        insideQuote = !insideQuote;
      } else if (char === ',' && !insideQuote) {
        row.push(entry.trim());
        entry = '';
      } else {
        entry += char;
      }
    }
    row.push(entry.trim());
    result.push(row);
  }
  return result;
};

// Helper to determine subject sort order based on subjects_code (e.g. 1, K10-1 -> 1, 10E-2 -> 2), subject_order, or name
const getSubjectSortScore = (sub) => {
  if (!sub) return 9999;
  
  // 1. Extract numeric code from subjects_code (e.g. "1", "01", "K10-1" -> 1, "10E-2" -> 2, "11-3" -> 3, "12-4A" -> 4.01)
  const rawCode = String(sub.subjects_code || sub.subject_code || sub.code || '').trim();
  if (rawCode) {
    const dashMatch = rawCode.match(/(?:^|[-_.\s])(\d+)([a-zA-Z]?)$/);
    if (dashMatch) {
      const mainNum = parseInt(dashMatch[1], 10);
      const letterSuffix = dashMatch[2] ? (dashMatch[2].toUpperCase().charCodeAt(0) - 64) * 0.01 : 0;
      return mainNum + letterSuffix;
    }
    const parsedCodeNum = parseFloat(rawCode);
    if (!isNaN(parsedCodeNum)) {
      return parsedCodeNum;
    }
  }

  // 2. Check explicit subject_order
  if (sub.subject_order !== undefined && sub.subject_order !== null && !isNaN(sub.subject_order) && Number(sub.subject_order) > 0) {
    return Number(sub.subject_order);
  }

  // 3. Extract code / number / Roman numeral from subject_name
  const name = String(sub.subject_name || '').trim().toUpperCase();
  const leadMatch = name.match(/^(\d+)[\s\.\-_:]/);
  if (leadMatch) {
    return parseInt(leadMatch[1], 10);
  }

  if (name.includes('I LANG') || name.includes('1ST LANG') || name.includes('/I LANG') || name.includes('1ST LANGUAGE')) {
    return 1;
  }
  if (name.includes('II LANG') || name.includes('2ND LANG') || name.includes('/II LANG') || name.includes('2ND LANGUAGE')) {
    return 2;
  }
  if (name.includes('III LANG') || name.includes('3RD LANG') || name.includes('/III LANG') || name.includes('3RD LANGUAGE')) {
    return 3;
  }
  if (name.includes('IV LANG') || name.includes('4TH LANG') || name.includes('/IV LANG')) {
    return 4;
  }

  if (name.includes('MATH') || name.includes('MATHEMATICS')) return 4.5;
  if (name.includes('SCI') || name.includes('SCIENCE') || name.includes('PHYSIC') || name.includes('CHEM')) return 5.5;
  if (name.includes('SOC') || name.includes('SOCIAL') || name.includes('BIOL')) return 6.5;

  return 100;
};

const CandidateManagementPage = () => {

  // --- Master Filters State ---
  const [academicYears, setAcademicYears] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [standards, setStandards] = useState([]);
  const [institutions, setInstitutions] = useState([]);

  const [selectedYearId, setSelectedYearId] = useState(undefined);
  const [selectedClusterId, setSelectedClusterId] = useState(undefined);
  const isStudio = (localStorage.getItem('role') || '').toUpperCase() === 'STUDIO';
  const [selectedStandardId, setSelectedStandardId] = useState(undefined);
  const [selectedClusterInstitutionId, setSelectedClusterInstitutionId] = useState(undefined);
  const [selectedInstitutionId, setSelectedInstitutionId] = useState(undefined);
  const [selectedInstitutionCode, setSelectedInstitutionCode] = useState(undefined);
  const [searchText, setSearchText] = useState('');

  // --- Subject Setup & Result Config State ---
  const [subjectConfigs, setSubjectConfigs] = useState([]);
  const [savingSubjects, setSavingSubjects] = useState(false);
  const [hasExistingConfigs, setHasExistingConfigs] = useState(false);

  const [resultConfig, setResultConfig] = useState({
    fail_max_pct: 32.99,
    pass_max_pct: 49.99,
    second_class_max_pct: 59.99,
    first_class_max_pct: 84.99,
    distinction_max_pct: 100
  });
  const [savingResultCfg, setSavingResultCfg] = useState(false);
  const [recalculating, setRecalculating] = useState(false);

  // --- Candidates Data State ---
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [pageSize, setPageSize] = useState(50);

  // --- Modal State ---
  const [isCandModalOpen, setIsCandModalOpen] = useState(false);
  const [editingCandidate, setEditingCandidate] = useState(null);
  const [candForm] = Form.useForm();

  // --- CSV Upload Modal State ---
  const [isCsvModalOpen, setIsCsvModalOpen] = useState(false);
  const [isBulkMarksModalOpen, setIsBulkMarksModalOpen] = useState(false);
  const [csvParsedData, setCsvParsedData] = useState([]);
  const [uploadingCsv, setUploadingCsv] = useState(false);
  const [activeTabKey, setActiveTabKey] = useState('analytics');
  const [hiddenColumns, setHiddenColumns] = useState([]);

  // --- Cluster & Institution Analytics State ---
  const [analytics, setAnalytics] = useState(null);
  const [analyticsFilterOptions, setAnalyticsFilterOptions] = useState({});
  

  const selectedYearName = academicYears.find(ay => ay.academic_year_id === selectedYearId)?.academic_year || 'Not Selected';
  const selectedClusterName = clusters.find(c => c.cluster_id === selectedClusterId)?.cluster_name || 'Not Selected';
  const selectedInstitutionName = institutions.find(i => 
    (selectedClusterInstitutionId && i.cluster_institution_id === selectedClusterInstitutionId) ||
    (i.institution_id === selectedInstitutionId && ((i.cluster_institution_code || i.institution_code || i.code || '') === (selectedInstitutionCode || '') || !selectedInstitutionCode))
  )?.institution_name || 'Not Selected';
  const selectedStandardName = standards.find(st => st.standard_id === selectedStandardId)?.standard_name || 'Not Selected';

  // Fetch Analytics & Filter options for Performance Analytics tab
  const loadAnalyticsData = useCallback(async () => {
    try {
      const [filterRes, analyticsRes] = await Promise.all([
        fetchDashboardFilterOptions(),
        fetchDashboardAnalytics({})
      ]);
      if (filterRes && filterRes.success) {
        setAnalyticsFilterOptions(filterRes);
      }
      if (analyticsRes && analyticsRes.success) {
        setAnalytics(analyticsRes);
      }
    } catch (err) {
      console.error('Error loading cluster analytics in candidates:', err);
    } finally {
    }
  }, []);

  useEffect(() => {
    loadAnalyticsData();
  }, [loadAnalyticsData]);

  // 1. Initial Load of Master Data (Defaulting to last completed Academic Year for Exam Metadata)
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const years = await fetchAcademicYears(true);
        const activeYears = (years || []).filter(y => y.status === 'Active' || !y.status);
        setAcademicYears(activeYears);
        if (activeYears && activeYears.length > 0) {
          const sortedYears = [...activeYears].sort((a, b) => b.academic_year_id - a.academic_year_id);
          const today = new Date();
          today.setHours(23, 59, 59, 999);
          const completedYears = sortedYears.filter(ay => {
            if (!ay.end_date) return false;
            const endDate = new Date(ay.end_date);
            return !isNaN(endDate.getTime()) && endDate <= today;
          });
          const lastCompletedAy = (completedYears && completedYears.length > 0) ? completedYears[0] : (sortedYears[1] || sortedYears[0]);
          setSelectedYearId((prev) => prev || lastCompletedAy.academic_year_id);
        }
      } catch (err) {
        console.error('Error loading master data:', err);
        message.error('Failed to load filter metadata.');
      }
    };
    loadInitialData();
  }, []);

  // 2. Fetch Clusters when Selected Academic Year changes (Filters Academic Year wise & Studio Access)
  useEffect(() => {
    fetchClusters(selectedYearId)
      .then((cls) => {
        const yearClusters = cls || [];
        const sortedClusters = [...yearClusters].sort((a, b) => (a.cluster_name || '').localeCompare(b.cluster_name || '', undefined, { numeric: true }));
        setClusters(sortedClusters);
        if (sortedClusters.length > 0) {
          setSelectedClusterId((prev) => {
            const exists = sortedClusters.some(c => c.cluster_id === prev);
            return exists ? prev : sortedClusters[0].cluster_id;
          });
        } else {
          setSelectedClusterId(undefined);
        }
      })
      .catch((err) => {
        console.error('Error fetching clusters academic year wise:', err);
        setClusters([]);
        setSelectedClusterId(undefined);
      });
  }, [selectedYearId]);


  // 3. Fetch Cluster Institutions when Cluster or Academic Year changes
  useEffect(() => {
    if (selectedClusterId) {
      fetchClusterInstitutions(selectedClusterId, selectedYearId)
        .then((res) => {
          const insts = res || [];
          const uniqueInsts = [];
          const seenKeys = new Set();
          insts.forEach((i) => {
            const ciId = i.cluster_institution_id;
            const iCode = i.cluster_institution_code || i.institution_code || i.code || '';
            const key = ciId ? `ci-${ciId}` : `${i.institution_id}-${iCode}`;
            if (i.institution_id && !seenKeys.has(key)) {
              seenKeys.add(key);
              uniqueInsts.push(i);
            }
          });
          const sortedInsts = [...uniqueInsts].sort((a, b) => (a.institution_name || '').localeCompare(b.institution_name || '', undefined, { numeric: true }));
          setInstitutions(sortedInsts);
          
          let defaultInst = sortedInsts.length > 0 ? sortedInsts[0] : null;
          setSelectedClusterInstitutionId((prev) => {
            if (prev && sortedInsts.some(i => i.cluster_institution_id === prev)) {
              return prev;
            }
            return defaultInst ? defaultInst.cluster_institution_id : undefined;
          });
          setSelectedInstitutionId((prev) => {
            if (prev && sortedInsts.some(i => i.institution_id === prev)) {
              return prev;
            }
            return defaultInst ? defaultInst.institution_id : undefined;
          });
          setSelectedInstitutionCode((prev) => {
            if (prev && sortedInsts.some(i => (i.cluster_institution_code || i.institution_code || i.code || '') === prev)) {
              return prev;
            }
            return defaultInst ? (defaultInst.cluster_institution_code || defaultInst.institution_code || defaultInst.code || undefined) : undefined;
          });
        })
        .catch((err) => {
          console.error('Error fetching cluster institutions:', err);
          setInstitutions([]);
          setSelectedClusterInstitutionId(undefined);
          setSelectedInstitutionId(undefined);
          setSelectedInstitutionCode(undefined);
        });
    } else {
      setInstitutions([]);
      setSelectedClusterInstitutionId(undefined);
      setSelectedInstitutionId(undefined);
      setSelectedInstitutionCode(undefined);
    }
  }, [selectedClusterId, selectedYearId]);

  // 4. Fetch Standards linked to Academic Year and Cluster
  useEffect(() => {
    fetchStandards(selectedYearId, selectedClusterId)
      .then((stds) => {
        const sortedStds = [...(stds || [])].sort((a, b) => (a.standard_name || '').localeCompare(b.standard_name || '', undefined, { numeric: true }));
        setStandards(sortedStds);
        if (sortedStds && sortedStds.length > 0) {
          setSelectedStandardId((prev) => {
            const exists = sortedStds.some(s => s.standard_id === prev);
            return exists ? prev : sortedStds[0].standard_id;
          });
        } else {
          setSelectedStandardId(undefined);
        }
      })
      .catch((err) => {
        console.error('Error fetching cluster standards:', err);
        setStandards([]);
        setSelectedStandardId(undefined);
      });
  }, [selectedYearId, selectedClusterId]);


  // 3. Load Configurations and Candidate Data when filters change
  const loadConfigsAndCandidates = useCallback(async () => {
    setLoading(true);
    const filterParams = {
      academic_year_id: selectedYearId,
      cluster_id: selectedClusterId,
      standard_id: selectedStandardId,
      cluster_institution_id: selectedClusterInstitutionId,
      institution_id: selectedInstitutionId,
      institution_code: selectedInstitutionCode,
      search: searchText
    };

    try {
      const [subs, resCfg, cands] = await Promise.all([
        fetchCandidateSubjectConfigs(filterParams),
        fetchCandidateResultConfig(filterParams),
        fetchCandidates(filterParams)
      ]);

      if (Array.isArray(subs)) {
        const sortedSubs = [...subs].sort((a, b) => getSubjectSortScore(a) - getSubjectSortScore(b));
        setSubjectConfigs(sortedSubs.map(s => ({
          ...s,
          old_subject_name: s.subject_name
        })));
        setHasExistingConfigs(subs.length > 0);
      }

      if (resCfg) {
        setResultConfig(resCfg);
      }

      setCandidates(cands || []);
    } catch (err) {
      console.error('Error loading candidates/configs:', err);
      message.error('Failed to fetch candidate details.');
    } finally {
      setLoading(false);
    }
  }, [selectedYearId, selectedClusterId, selectedStandardId, selectedClusterInstitutionId, selectedInstitutionId, selectedInstitutionCode, searchText]);

  useEffect(() => {
    loadConfigsAndCandidates();
  }, [loadConfigsAndCandidates]);

  // --- Handlers for Subject Setup ---
  const handleAddSubjectField = () => {
    setSubjectConfigs([
      ...subjectConfigs,
      { subject_config_id: null, subject_name: `Subject ${subjectConfigs.length + 1}`, old_subject_name: null, max_mark: 100, min_mark: 30 }
    ]);
  };

  const handleSubjectChange = (index, field, value) => {
    const updated = [...subjectConfigs];
    updated[index] = { ...updated[index], [field]: value };
    setSubjectConfigs(updated);
  };

  const handleRemoveSubjectField = (index) => {
    const updated = subjectConfigs.filter((_, i) => i !== index);
    setSubjectConfigs(updated);
  };

  const handleSaveSubjectConfigs = async () => {
    setSavingSubjects(true);
    try {
      await saveCandidateSubjectConfigs({
        academic_year_id: selectedYearId,
        cluster_id: selectedClusterId,
        standard_id: selectedStandardId,
        institution_id: selectedInstitutionId,
        subjects: subjectConfigs.map((s, idx) => ({
          subject_config_id: s.subject_config_id || null,
          subject_name: s.subject_name,
          old_subject_name: s.old_subject_name || null,
          max_mark: s.max_mark,
          min_mark: s.min_mark,
          subject_order: idx + 1
        }))
      });
      message.success(hasExistingConfigs ? 'Subject setup updated successfully!' : 'Subject setup saved successfully!');
      
      // Update our track state
      setHasExistingConfigs(true);

      // Prompt to recalculate
      Modal.confirm({
        title: 'Recalculate Results?',
        content: 'Subject configurations have been updated. Do you want to recalculate results for all candidates under this standard now to apply the new configurations?',
        okText: 'Yes, Recalculate',
        cancelText: 'No',
        onOk: async () => {
          setRecalculating(true);
          try {
            const res = await recalculateCandidateResults({
              academic_year_id: selectedYearId,
              cluster_id: selectedClusterId,
              standard_id: selectedStandardId,
              institution_id: selectedInstitutionId
            });
            message.success(res.message || 'Recalculated results successfully!');
            loadConfigsAndCandidates();
            loadAnalyticsData();
            window.dispatchEvent(new CustomEvent('candidatesUpdated'));
          } catch (err) {
            message.error(err.message || 'Failed to recalculate results.');
          } finally {
            setRecalculating(false);
          }
        }
      });

      loadConfigsAndCandidates();
      window.dispatchEvent(new CustomEvent('candidatesUpdated'));
    } catch (err) {
      message.error(err.message || 'Failed to save subject configuration.');
    } finally {
      setSavingSubjects(false);
    }
  };

  // --- Handlers for Result Thresholds ---
  const handleSaveResultConfig = async () => {
    setSavingResultCfg(true);
    try {
      await saveCandidateResultConfig({
        academic_year_id: selectedYearId,
        cluster_id: selectedClusterId,
        standard_id: selectedStandardId,
        institution_id: selectedInstitutionId,
        ...resultConfig
      });
      message.success('Result classification rules saved successfully!');
      loadConfigsAndCandidates();
      window.dispatchEvent(new CustomEvent('candidatesUpdated'));
    } catch (err) {
      message.error(err.message || 'Failed to save result rules.');
    } finally {
      setSavingResultCfg(false);
    }
  };

  const handleRecalculateSelected = async () => {
    if (selectedRowKeys.length === 0) return;
    setRecalculating(true);
    try {
      const payload = {
        academic_year_id: selectedYearId,
        cluster_id: selectedClusterId,
        standard_id: selectedStandardId,
        institution_id: selectedInstitutionId,
        candidate_ids: selectedRowKeys
      };
      const res = await recalculateCandidateResults(payload);
      message.success(res.message || 'Recalculated results successfully!');
      setSelectedRowKeys([]);
      loadConfigsAndCandidates();
      loadAnalyticsData();
      window.dispatchEvent(new CustomEvent('candidatesUpdated'));
    } catch (error) {
      message.error(error.message || 'Failed to recalculate results.');
    } finally {
      setRecalculating(false);
    }
  };

  // --- Candidate CRUD Handlers ---
  const handleOpenAddCandidateModal = () => {
    setEditingCandidate(null);
    candForm.resetFields();
    candForm.setFieldsValue({ override_result: 'AUTO', marks: {} });
    setIsCandModalOpen(true);
  };

const getMarkVal = (marksObj, subjectName) => {
  if (!marksObj || !subjectName) return undefined;
  if (marksObj[subjectName] !== undefined && marksObj[subjectName] !== null && marksObj[subjectName] !== '') {
    return marksObj[subjectName];
  }
  const sClean = String(subjectName).trim().toLowerCase();
  for (const [key, val] of Object.entries(marksObj)) {
    if (String(key).trim().toLowerCase() === sClean && val !== undefined && val !== null && val !== '') {
      return val;
    }
  }
  return undefined;
};

  const handleOpenEditCandidateModal = (record) => {
    setEditingCandidate(record);
    const candidateMarks = {};
    displaySubjects.forEach((s) => {
      const mVal = getMarkVal(record.marks, s.subject_name);
      if (mVal !== undefined) {
        candidateMarks[s.subject_name] = mVal;
      }
    });

    candForm.setFieldsValue({
      name: record.name,
      parent_name: record.parent_name || '',
      gender: record.gender,
      contact: record.contact,
      dob: record.dob,
      email: record.email,
      combination: record.combination || '',
      mode: record.mode || '',
      cluster_institution_id: record.cluster_institution_id || record.resolved_cluster_institution_id || record.institution_id,
      institution_id: record.institution_id,
      override_result: record.result || 'AUTO',
      marks: candidateMarks
    });
    setIsCandModalOpen(true);
  };

  const handleSaveCandidate = async (values) => {
    try {
      const cleanedMarks = {};
      if (values.marks) {
        Object.entries(values.marks).forEach(([subName, markVal]) => {
          if (markVal !== undefined && markVal !== null && markVal !== '') {
            cleanedMarks[subName] = markVal;
          }
        });
      }

      const payload = {
        academic_year_id: selectedYearId,
        cluster_id: selectedClusterId,
        standard_id: selectedStandardId,
        cluster_institution_id: selectedClusterInstitutionId || values.cluster_institution_id,
        institution_id: selectedInstitutionId || values.institution_id,
        login_id: values.login_id || values.user_id || null,
        user_id: values.login_id || values.user_id || null,
        ...values,
        marks: cleanedMarks
      };

      if (editingCandidate) {
        await updateCandidate(editingCandidate.candidate_id, payload);
        message.success('Candidate details updated successfully!');
      } else {
        await createCandidate(payload);
        message.success('Candidate added successfully!');
      }

      setIsCandModalOpen(false);
      loadConfigsAndCandidates();
      loadAnalyticsData();
      window.dispatchEvent(new CustomEvent('candidatesUpdated'));
      setActiveTabKey('view');
    } catch (err) {
      message.error(err.message || 'Failed to save candidate.');
    }
  };

  const handleDeleteCandidate = async (candidateId) => {
    try {
      await deleteCandidate(candidateId);
      message.success('Candidate deleted successfully!');
      loadConfigsAndCandidates();
      loadAnalyticsData();
      window.dispatchEvent(new CustomEvent('candidatesUpdated'));
    } catch (err) {
      message.error(err.message || 'Failed to delete candidate.');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRowKeys.length === 0) return;
    try {
      await bulkDeleteCandidates(selectedRowKeys);
      message.success(`Successfully deleted ${selectedRowKeys.length} candidate(s)!`);
      setSelectedRowKeys([]);
      loadConfigsAndCandidates();
      loadAnalyticsData();
      window.dispatchEvent(new CustomEvent('candidatesUpdated'));
    } catch (err) {
      message.error(err.message || 'Failed to delete selected candidates.');
    }
  };

  // --- CSV Download & Template Generator ---
  const handleDownloadSampleTemplate = () => {
    const headers = [];
    const sampleRow = [];

    if (!selectedInstitutionId) {
      headers.push('CODE');
      sampleRow.push('C1-005');
    }

    headers.push('NAME', 'GENDER', 'PARENT / GUARDIAN NAME', 'CONTACT', 'D-O-B', 'E-MAIL', 'COMBINATION', 'MODE');
    sampleRow.push('Rahul Kumar', 'Male', 'Suresh Kumar', '9876543210', '2010-05-15', 'rahul@example.com', 'HEBA', 'Regular');

    displaySubjects.forEach((s) => headers.push(s.subject_name));
    displaySubjects.forEach((s) => sampleRow.push(s.max_mark ? Math.round(s.max_mark * 0.75) : 75));

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), sampleRow.join(',')].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', 'Candidates_Import_Template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadCandidatesCSV = () => {
    if (candidates.length === 0) {
      message.warning('No candidate records available to export.');
      return;
    }

    const selectedInst = institutions.find(i => Number(i.institution_id) === Number(selectedInstitutionId) && ((i.cluster_institution_code || i.institution_code || i.code || '') === (selectedInstitutionCode || '') || !selectedInstitutionCode));
    const instName = selectedInst?.institution_name || selectedInstitutionName || 'N/A';
    const instHead = selectedInst?.institution_head || 'N/A';
    const instContact = selectedInst?.institution_contact || 'N/A';
    const instAddress = selectedInst?.institution_address || 'N/A';

    const rows = [];

    // Row-Wise Institution & Exam Metadata Details
    rows.push(`"INSTITUTION NAME: ${instName}"`);
    if (selectedInst?.cluster_institution_code) {
      rows.push(`"INSTITUTION CODE: ${selectedInst.cluster_institution_code}"`);
    }
    rows.push(`"INSTITUTION HEAD: ${instHead}"`);
    rows.push(`"CONTACT: ${instContact}"`);
    rows.push(`"ADDRESS: ${instAddress}"`);
    rows.push(`"ACADEMIC YEAR: ${selectedYearName}"`);
    rows.push(`"CLUSTER: ${selectedClusterName}"`);
    rows.push(`"STANDARD: ${selectedStandardName}"`);
    rows.push(''); // Empty separator row

    // Row 4: Column Headers
    const headers = ['SL. NO.', 'CANDIDATE ID', 'CODE', 'NAME', 'GENDER', 'PARENT / GUARDIAN NAME', 'CONTACT', 'D-O-B', 'E-MAIL', 'COMBINATION', 'MODE'];
    displaySubjects.forEach((s) => headers.push(s.subject_name));
    headers.push(`TOTAL (${Math.round(totalMaxPossible)})`, 'Percentage', 'Result');
    rows.push(headers.join(','));

    // Candidate Rows
    candidates.forEach((c, index) => {
      const codeVal = c.code || c.institution_code || c.cluster_institution_code || selectedInst?.cluster_institution_code || '';
      const row = [
        index + 1,
        c.candidate_id || '',
        `"${codeVal}"`,
        `"${c.name || ''}"`,
        `"${c.gender || ''}"`,
        `"${c.parent_name || ''}"`,
        `"${c.contact || ''}"`,
        `"${c.dob || ''}"`,
        `"${c.email || ''}"`,
        `"${c.combination || ''}"`,
        `"${c.mode || ''}"`
      ];

      displaySubjects.forEach((s) => {
        const markVal = getMarkVal(c.marks, s.subject_name);
        const displayVal = markVal !== undefined ? Math.round(markVal) : '';
        row.push(displayVal);
      });

      row.push(Math.round(c.total_marks || 0));
      row.push(`${parseFloat(c.percentage || 0).toFixed(2)}%`);
      row.push(`"${c.result || ''}"`);

      rows.push(row.join(','));
    });

    const csvContent = 'data:text/csv;charset=utf-8,' + rows.join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `${instName.replace(/[^a-zA-Z0-9]/g, '_')}_Candidates_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // --- CSV Upload Handling ---
  const handleCsvFileUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const parsed = parseCSVText(text);

      if (parsed.length < 2) {
        message.error('CSV file must contain a header row and at least one data row.');
        return;
      }

      // Auto-find actual column header row (skipping row-wise institution metadata rows if present)
      let headerRowIndex = 0;
      for (let i = 0; i < Math.min(parsed.length, 15); i++) {
        const rowStr = parsed[i].join(' ').toUpperCase();
        if (!rowStr.includes('INSTITUTION NAME:') && !rowStr.includes('ACADEMIC YEAR:') && (rowStr.includes('GENDER') || rowStr.includes('SL. NO') || (rowStr.includes('NAME') && !rowStr.includes('INSTITUTION')))) {
          headerRowIndex = i;
          break;
        }
      }

      const headers = parsed[headerRowIndex].map((h) => h.trim().toUpperCase());
      const rows = parsed.slice(headerRowIndex + 1);

      const parsedCandidates = [];

      rows.forEach((rowArr) => {
        if (rowArr.length === 0 || !rowArr.some((cell) => cell.trim() !== '')) return;

        const candidateItem = {
          candidate_id: null,
          name: '',
          code: '',
          gender: '',
          parent_name: '',
          contact: '',
          dob: '',
          email: '',
          combination: '',
          mode: '',
          marks: {}
        };

        headers.forEach((h, idx) => {
          const val = rowArr[idx] ? rowArr[idx].trim() : '';

          if (h.includes('CANDIDATE ID') || h.includes('CANDIDATE_ID') || h === 'CANDIDATE ID' || h === 'CANDIDATE_ID') {
            const parsedId = parseInt(val);
            candidateItem.candidate_id = !isNaN(parsedId) ? parsedId : null;
          } else if (h.includes('NAME') && !h.includes('PARENT') && !h.includes('GUARDIAN') && !h.includes('FATHER') && !h.includes('MOTHER') && !h.includes('INSTITUTION') && !h.includes('FILE')) {
            candidateItem.name = val;
          } else if (h.includes('GENDER')) {
            candidateItem.gender = val;
          } else if (h.includes('PARENT') || h.includes('GUARDIAN') || h.includes('FATHER') || h.includes('MOTHER')) {
            candidateItem.parent_name = val;
          } else if (h.includes('CONTACT') || h.includes('PHONE')) {
            candidateItem.contact = val;
          } else if (h.includes('DOB') || h.includes('D-O-B') || h.includes('BIRTH')) {
            candidateItem.dob = val;
          } else if (h.includes('EMAIL') || h.includes('E-MAIL')) {
            candidateItem.email = val;
          } else if (h.includes('COMBINATION') || h.includes('COMB')) {
            candidateItem.combination = val;
          } else if (h.includes('MODE')) {
            candidateItem.mode = val;
          } else if (h === 'CODE' || h.includes('SCHOOL CODE') || h.includes('INSTITUTION CODE') || h.includes('SCHOOL_CODE') || h.includes('INSTITUTION_CODE')) {
            candidateItem.code = val;
          } else if (h.includes('RESULT') || h.includes('STATUS') || h.includes('OVERRIDE')) {
            candidateItem.override_result = val ? val.toUpperCase() : 'AUTO';
          } else {
            // Check if column header matches one of configured subjects (by name, code, or normalized name)
            const matchedSub = subjectConfigs.find((s) => {
              const sName = String(s.subject_name || '').trim().toUpperCase();
              const sCode = String(s.subjects_code || s.subject_code || s.code || '').trim().toUpperCase();
              return sName === h || (sCode && sCode === h) || sName.replace(/[\s\-_/]/g, '') === h.replace(/[\s\-_/]/g, '');
            });
            if (matchedSub) {
              const rawCell = val ? val.trim() : '';
              if (rawCell !== '' && rawCell.toUpperCase() !== 'N/A' && rawCell !== '-') {
                const parsedNum = parseFloat(rawCell);
                if (!isNaN(parsedNum)) {
                  candidateItem.marks[matchedSub.subject_name] = Math.round(parsedNum);
                }
              }
            }
          }
        });

        // Safe fallback name if name column is empty/missing
        if (!candidateItem.name) {
          candidateItem.name = `Candidate #${parsedCandidates.length + 1}`;
        }
        parsedCandidates.push(candidateItem);
      });

      if (parsedCandidates.length === 0) {
        message.error('No valid candidate records found in CSV file.');
        return;
      }

      setCsvParsedData(parsedCandidates);
      setIsCsvModalOpen(true);
    };

    reader.readAsText(file);
    return false; // Prevent automatic upload
  };

  const handleConfirmCsvUpload = async () => {
    setUploadingCsv(true);
    try {
      await bulkUploadCandidates({
        academic_year_id: selectedYearId,
        cluster_id: selectedClusterId,
        standard_id: selectedStandardId,
        cluster_institution_id: selectedClusterInstitutionId,
        institution_id: selectedInstitutionId,
        candidates: csvParsedData
      });
      message.success(`Successfully imported ${csvParsedData.length} candidates!`);
      setIsCsvModalOpen(false);
      setCsvParsedData([]);
      loadConfigsAndCandidates();
      loadAnalyticsData();
      window.dispatchEvent(new CustomEvent('candidatesUpdated'));
      setActiveTabKey('view');
    } catch (err) {
      message.error(err.message || 'Failed to import CSV candidates.');
    } finally {
      setUploadingCsv(false);
    }
  };

  // --- Dynamic Table Columns Generation ---
  // Show subject columns in exact code / subject_order sequence (Code 1 / 01 / K10-1 first)
  const displaySubjects = useMemo(() => {
    const subjectMap = new Map();

    // 1. Add configured subjects
    (subjectConfigs || []).forEach((s) => {
      if (s && s.subject_name) {
        const cleanKey = String(s.subject_name).trim().toLowerCase();
        if (!subjectMap.has(cleanKey)) {
          subjectMap.set(cleanKey, {
            subject_config_id: s.subject_config_id,
            subject_name: s.subject_name.trim(),
            subjects_code: s.subjects_code || s.subject_code || s.code || '',
            subject_order: s.subject_order,
            max_mark: s.max_mark || 100,
            min_mark: s.min_mark || 30
          });
        }
      }
    });

    // 2. Append any additional subject columns added in candidate records
    (candidates || []).forEach((c) => {
      if (c && c.marks && typeof c.marks === 'object') {
        Object.keys(c.marks).forEach((key) => {
          if (key && String(key).trim().length > 0) {
            const cleanKey = String(key).trim().toLowerCase();
            if (!subjectMap.has(cleanKey)) {
              subjectMap.set(cleanKey, {
                subject_name: key.trim(),
                subjects_code: '',
                subject_order: 999,
                max_mark: 100,
                min_mark: 30
              });
            }
          }
        });
      }
    });

    const subjectsList = Array.from(subjectMap.values());
    return subjectsList.sort((a, b) => {
      const scoreA = getSubjectSortScore(a);
      const scoreB = getSubjectSortScore(b);
      if (scoreA !== scoreB) return scoreA - scoreB;
      const codeA = String(a.subjects_code || a.subject_code || a.code || '');
      const codeB = String(b.subjects_code || b.subject_code || b.code || '');
      if (codeA && codeB) return codeA.localeCompare(codeB, undefined, { numeric: true });
      return (a.subject_name || '').localeCompare(b.subject_name || '');
    });
  }, [subjectConfigs, candidates]);

  const totalMaxPossible = Math.round(displaySubjects.reduce((sum, s) => sum + parseFloat(s.max_mark || 100), 0));
  const selectedInstRecord = institutions.find(i => 
    (selectedClusterInstitutionId && Number(i.cluster_institution_id) === Number(selectedClusterInstitutionId)) ||
    (Number(i.institution_id) === Number(selectedInstitutionId) && ((i.cluster_institution_code || i.institution_code || i.code || '') === (selectedInstitutionCode || '') || !selectedInstitutionCode))
  );
  const currentInstitutionCode = selectedInstRecord?.cluster_institution_code || selectedInstRecord?.institution_code || selectedInstRecord?.code || '';

  const columns = [
    {
      title: 'CANDIDATE DETAILS',
      children: [
        {
          title: 'CODE',
          dataIndex: 'code',
          key: 'code',
          width: 100,
          align: 'center',
          sorter: (a, b) => (a.code || a.institution_code || a.cluster_institution_code || currentInstitutionCode || '').localeCompare(b.code || b.institution_code || b.cluster_institution_code || currentInstitutionCode || ''),
          render: (val, record) => {
            const displayCode = val || record.institution_code || record.cluster_institution_code || currentInstitutionCode || '-';
            return <Tag color="geekblue" style={{ fontWeight: 700 }}>{displayCode}</Tag>;
          }
        },
        {
          title: 'NAME',
          dataIndex: 'name',
          key: 'name',
          width: 140,
          sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
          render: (text) => <strong style={{ color: '#1890ff' }}>{text}</strong>
        },
        {
          title: 'GENDER',
          dataIndex: 'gender',
          key: 'gender',
          width: 75,
          render: (val) => val || '-'
        },
        {
          title: 'PARENT / GUARDIAN NAME',
          dataIndex: 'parent_name',
          key: 'parent_name',
          width: 150,
          sorter: (a, b) => (a.parent_name || '').localeCompare(b.parent_name || ''),
          render: (val) => val || '-'
        },
        {
          title: 'CONTACT',
          dataIndex: 'contact',
          key: 'contact',
          width: 105,
          render: (val) => val || '-'
        },
        {
          title: 'D-O-B',
          dataIndex: 'dob',
          key: 'dob',
          width: 95,
          render: (val) => val || '-'
        },
        {
          title: 'E-MAIL',
          dataIndex: 'email',
          key: 'email',
          width: 140,
          render: (val) => val || '-'
        },
        {
          title: 'COMBINATION',
          dataIndex: 'combination',
          key: 'combination',
          width: 120,
          filters: Array.from(new Set(candidates.map(c => c.combination).filter(Boolean)))
            .map(comb => ({ text: comb, value: comb })),
          onFilter: (value, record) => record.combination === value,
          render: (val) => val || '-'
        },
        {
          title: 'MODE',
          dataIndex: 'mode',
          key: 'mode',
          width: 120,
          render: (val) => val || '-'
        }
      ]
    },
    ...(displaySubjects.length > 0
      ? [
          {
            title: 'FINAL EXAM MARKS',
            children: displaySubjects.map((sub) => ({
              title: (
                <Tooltip title={`${sub.subjects_code ? `Code: ${sub.subjects_code} | ` : ''}Max: ${Math.round(sub.max_mark || 100)} | Min: ${Math.round(sub.min_mark || 30)}`}>
                  <span>{sub.subject_name}</span>
                </Tooltip>
              ),
              key: `subject_${sub.subject_name}`,
              width: 85,
              align: 'center',
              sorter: (a, b) => {
                const markA = getMarkVal(a.marks, sub.subject_name);
                const markB = getMarkVal(b.marks, sub.subject_name);
                const valA = markA !== undefined ? parseFloat(markA) : -1;
                const valB = markB !== undefined ? parseFloat(markB) : -1;
                return valA - valB;
              },
              render: (_, record) => {
                const markVal = getMarkVal(record.marks, sub.subject_name);

                if (markVal === undefined) {
                  return <span style={{ color: '#bfbfbf' }}>-</span>;
                }

                const val = Math.round(parseFloat(markVal) || 0);
                const isBelowMin = val < Math.round(parseFloat(sub.min_mark || 30));
                return (
                  <span
                    style={{
                      color: isBelowMin ? '#ff4d4f' : 'inherit',
                      fontWeight: isBelowMin ? 'bold' : 'normal'
                    }}
                  >
                    {val}
                  </span>
                );
              }
            }))
          }
        ]
      : []),
    {
      title: `TOTAL (${totalMaxPossible})`,
      dataIndex: 'total_marks',
      key: 'total_marks',
      width: 90,
      align: 'center',
      sorter: (a, b) => parseFloat(a.total_marks || 0) - parseFloat(b.total_marks || 0),
      render: (val, record) => (
        <strong>
          {Math.round(parseFloat(val) || 0)} / {Math.round(parseFloat(record.total_max_marks || totalMaxPossible))}
        </strong>
      )
    },
    {
      title: 'Percentage',
      dataIndex: 'percentage',
      key: 'percentage',
      width: 85,
      align: 'center',
      sorter: (a, b) => parseFloat(a.percentage || 0) - parseFloat(b.percentage || 0),
      render: (val) => (
        <span style={{ fontWeight: 600 }}>
          {val !== undefined && val !== null ? `${parseFloat(val || 0).toFixed(2)}%` : '0.00%'}
        </span>
      )
    },
    {
      title: 'Result',
      dataIndex: 'result',
      key: 'result',
      width: 110,
      align: 'center',
      sorter: (a, b) => (a.result || '').localeCompare(b.result || ''),
      render: (result) => {
        let color = 'green';
        if (result === 'FAIL') color = 'red';
        else if (result === 'SECOND CLASS') color = 'orange';
        else if (result === 'FIRST CLASS') color = 'blue';
        else if (result === 'DISTINCTION') color = 'purple';
        return <Tag color={color}>{result || 'N/A'}</Tag>;
      }
    },
    {
      title: 'Action',
      key: 'action',
      width: 70,
      fixed: candidates && candidates.length > 0 ? 'right' : undefined,
      align: 'center',
      render: (_, record) => (
        <Space size={2}>
          <Tooltip title="Edit Candidate">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined style={{ color: '#1890ff' }} />}
              onClick={() => handleOpenEditCandidateModal(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this candidate?"
            onConfirm={() => handleDeleteCandidate(record.candidate_id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete Candidate">
              <Button type="text" size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const allColumnOptions = [
    { label: 'School Code', value: 'code' },
    { label: 'Name', value: 'name' },
    { label: 'Gender', value: 'gender' },
    { label: 'Parent / Guardian Name', value: 'parent_name' },
    { label: 'Contact', value: 'contact' },
    { label: 'D-O-B', value: 'dob' },
    { label: 'E-mail', value: 'email' },
    { label: 'Combination', value: 'combination' },
    { label: 'Mode', value: 'mode' },
    ...(displaySubjects.map(sub => ({ label: sub.subject_name, value: `subject_${sub.subject_name}` }))),
    { label: 'Total Marks', value: 'total_marks' },
    { label: 'Percentage', value: 'percentage' },
    { label: 'Result', value: 'result' },
    { label: 'Action', value: 'action' }
  ];

  const filterColumns = (cols) => {
    return cols.map(col => {
      if (col.children) {
        const filteredChildren = filterColumns(col.children);
        if (filteredChildren.length === 0) return null;
        return { ...col, children: filteredChildren };
      }
      if (hiddenColumns.includes(col.key || col.dataIndex)) return null;
      return col;
    }).filter(Boolean);
  };

  const finalColumns = filterColumns(columns);

  const isAllMetadataSelected = selectedYearId && selectedClusterId && selectedStandardId;

  const renderMetadataFilterBar = () => (
    <Card
      title={
        <Space>
          <SettingOutlined style={{ color: '#1890ff' }} />
          <span>SELECT EXAM METADATA (ACADEMIC YEAR, CLUSTER, INSTITUTION &amp; STANDARD)</span>
        </Space>
      }
      bordered
      style={{ marginBottom: 16 }}
    >
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={6}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>ACADEMIC YEAR:</div>
          <Select showSearch optionFilterProp="children"
            style={{ width: '100%' }}
            placeholder="Select Academic Year"
            value={selectedYearId}
            onChange={(val) => setSelectedYearId(val)}
            allowClear
          >
            {academicYears.map((ay) => (
              <Option key={ay.academic_year_id} value={ay.academic_year_id}>
                {ay.academic_year}
              </Option>
            ))}
          </Select>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>CLUSTER:</div>
          <Select showSearch optionFilterProp="children"
            style={{ width: '100%' }}
            placeholder="Select Cluster"
            value={selectedClusterId}
            onChange={(val) => {
              if (!val && isStudio && clusters.length > 0) {
                setSelectedClusterId(clusters[0].cluster_id);
              } else {
                setSelectedClusterId(val);
              }
            }}
            allowClear={!isStudio}
          >
            {clusters.map((c) => (
              <Option key={c.cluster_id} value={c.cluster_id}>
                {c.cluster_name} ({c.cluster_code}) {c.academic_year ? `- [${c.academic_year}]` : ''}
              </Option>
            ))}
          </Select>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>INSTITUTION NAME:</div>
          <Select showSearch optionFilterProp="children"
            style={{ width: '100%' }}
            placeholder="Select Institution"
            value={selectedClusterInstitutionId ? String(selectedClusterInstitutionId) : (selectedInstitutionId ? `${selectedInstitutionId}||${selectedInstitutionCode || ''}` : undefined)}
            onChange={(val) => {
              if (val) {
                const foundInst = institutions.find(i => String(i.cluster_institution_id) === String(val) || `${i.institution_id}||${i.cluster_institution_code || i.institution_code || i.code || ''}` === val);
                if (foundInst) {
                  setSelectedClusterInstitutionId(foundInst.cluster_institution_id || undefined);
                  setSelectedInstitutionId(foundInst.institution_id);
                  setSelectedInstitutionCode(foundInst.cluster_institution_code || foundInst.institution_code || foundInst.code || undefined);
                } else if (val.includes('||')) {
                  const [id, ...codeParts] = val.split('||');
                  const code = codeParts.join('||');
                  setSelectedClusterInstitutionId(undefined);
                  setSelectedInstitutionId(parseInt(id));
                  setSelectedInstitutionCode(code || undefined);
                }
              } else {
                setSelectedClusterInstitutionId(undefined);
                setSelectedInstitutionId(undefined);
                setSelectedInstitutionCode(undefined);
              }
            }}
            allowClear
          >
            {institutions.map((inst) => {
              const instCode = inst.cluster_institution_code || inst.institution_code || inst.code || '';
              const label = instCode ? `${instCode} - ${inst.institution_name}` : inst.institution_name;
              const optionVal = inst.cluster_institution_id ? String(inst.cluster_institution_id) : `${inst.institution_id}||${instCode}`;
              return (
                <Option key={optionVal} value={optionVal}>
                  {label}
                </Option>
              );
            })}
          </Select>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>STANDARD:</div>
          <Select showSearch optionFilterProp="children"
            style={{ width: '100%' }}
            placeholder="Select Standard"
            value={selectedStandardId}
            onChange={(val) => setSelectedStandardId(val)}
            allowClear
          >
            {standards.map((st) => (
              <Option key={st.standard_id} value={st.standard_id}>
                {st.standard_name} {st.standard_medium ? `(${st.standard_medium})` : ''}
              </Option>
            ))}
          </Select>
        </Col>
      </Row>
    </Card>
  );

  const tabItems = [
    {
      key: 'analytics',
      label: (
        <Space>
          <ClusterOutlined />
          <span>Candidates Performance Analytics</span>
        </Space>
      ),
      children: (
        <div style={{ marginTop: 8 }}>
          <ClusterInstitutionAnalyticsCard
            InstitutionsData={analytics?.clusterInstitutionStats}
            academicYears={analyticsFilterOptions?.academicYears || academicYears}
            clusters={analyticsFilterOptions?.clusters || clusters}
          />
        </div>
      )
    },
    {
      key: 'view',
      label: (
        <Space>
          <TableOutlined />
          <span>VIEW UPLOADED DATA</span>
          <Badge count={candidates.length} overflowCount={9999} style={{ backgroundColor: '#52c41a', marginLeft: 4 }} />
        </Space>
      ),
      children: (
        <div>
          {renderMetadataFilterBar()}
          <Alert
            type="success"
            showIcon
            icon={<FilterOutlined />}
            message={
              <Space wrap style={{ fontSize: '13px' }}>
                <strong>Viewing Data Filtered By:</strong>
                <Tag color="blue">Academic Year: {selectedYearName}</Tag>
                <Tag color="purple">Cluster: {selectedClusterName}</Tag>
                <Tag color="cyan">Institution: {selectedInstitutionName} {currentInstitutionCode ? `(${currentInstitutionCode})` : ''}</Tag>
                <Tag color="green">Standard: {selectedStandardName}</Tag>
              </Space>
            }
            style={{ marginBottom: 16 }}
          />

          <Card
            title={
              <Space>
                <UserAddOutlined style={{ color: '#1890ff' }} />
                <span>UPLOADED CANDIDATES &amp; MARKS RECORDS</span>
              </Space>
            }
            extra={
              <Space wrap>
                {selectedRowKeys.length > 0 && (
                  <>
                    <Popconfirm
                      title={`Recalculate selected ${selectedRowKeys.length} candidate(s)?`}
                      onConfirm={handleRecalculateSelected}
                      okText="Yes"
                      cancelText="No"
                    >
                      <Button type="primary" icon={<ReloadOutlined />} loading={recalculating}>
                        Recalculate Selected ({selectedRowKeys.length})
                      </Button>
                    </Popconfirm>
                    <Popconfirm
                      title={`Delete selected ${selectedRowKeys.length} candidate(s)?`}
                      onConfirm={handleBulkDelete}
                      okText="Yes"
                      cancelText="No"
                    >
                      <Button danger type="primary" icon={<DeleteOutlined />}>
                        Delete Selected ({selectedRowKeys.length})
                      </Button>
                    </Popconfirm>
                  </>
                )}
                <Input
                  placeholder="Search candidate..."
                  prefix={<SearchOutlined />}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{ width: 220 }}
                  allowClear
                />
                <Select showSearch optionFilterProp="children"
                  mode="multiple"
                  placeholder="Hide Columns"
                  value={hiddenColumns}
                  onChange={setHiddenColumns}
                  style={{ width: 220 }}
                  options={allColumnOptions}
                  maxTagCount={1}
                  allowClear
                />
                <Button icon={<FileExcelOutlined style={{ color: '#52c41a' }} />} onClick={handleDownloadCandidatesCSV}>
                  Export CSV
                </Button>
                <Upload beforeUpload={handleCsvFileUpload} showUploadList={false} accept=".csv">
                  <Button type="primary" style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }} icon={<UploadOutlined />}>
                    Bulk Upload Marks
                  </Button>
                </Upload>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenAddCandidateModal}>
                  Add Candidate
                </Button>
              </Space>
            }
            bordered
          >
          <div className="excel-table-container">
            <style>{`
              .excel-table-container .ant-table-cell {
                padding: 2px 6px !important;
                line-height: 1.15 !important;
                font-size: 12px !important;
              }
              .excel-table-container .ant-table-thead > tr > th {
                padding: 4px 6px !important;
                line-height: 1.2 !important;
                font-size: 11px !important;
              }
              .excel-table-container .ant-tag {
                margin-right: 0 !important;
                padding: 0 4px !important;
                font-size: 11px !important;
                line-height: 18px !important;
              }
              .excel-table-container .ant-btn-text {
                height: 20px !important;
                width: 20px !important;
                padding: 0 !important;
              }
            `}</style>
            <Table
              size="small"
              rowSelection={candidates && candidates.length > 0 ? { selectedRowKeys, onChange: (keys) => setSelectedRowKeys(keys) } : undefined}
              columns={finalColumns}
              dataSource={candidates}
              rowKey="candidate_id"
              loading={loading}
              bordered
              scroll={candidates && candidates.length > 0 ? { x: 'max-content' } : undefined}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <div style={{ padding: '16px 0' }}>
                        {!isAllMetadataSelected ? (
                          <div>
                            <Text strong style={{ fontSize: 14, color: '#fa8c16' }}>
                              Please select Academic Year, Cluster, and Standard from the filter bar above
                            </Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              Candidate records and marks are grouped by Academic Year, Cluster, and Standard.
                            </Text>
                          </div>
                        ) : (
                          <div>
                            <Text strong style={{ fontSize: 14, color: '#64748b' }}>
                              No uploaded candidate records or marks found for the selected metadata
                            </Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              Click "Add Candidate" or switch to "UPLOAD CANDIDATE DATA & SETUP" tab to upload CSV data.
                            </Text>
                          </div>
                        )}
                      </div>
                    }
                  />
                )
              }}
              pagination={
                candidates && candidates.length > 0
                  ? {
                      pageSize: pageSize,
                      pageSizeOptions: ['50', '100', '200', '500', '1000'],
                      showSizeChanger: true,
                      onShowSizeChange: (current, size) => setPageSize(size),
                      onChange: (page, size) => setPageSize(size),
                      showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} Candidates`
                    }
                  : false
              }
            />
          </div>
          </Card>
        </div>
      )
    },
    {
      key: 'upload',
      label: (
        <Space>
          <CloudUploadOutlined />
          <span>UPLOAD CANDIDATE DATA &amp; SETUP</span>
        </Space>
      ),
      children: (
        <div>
          {renderMetadataFilterBar()}
          {!isAllMetadataSelected ? (
            <Alert
              type="warning"
              showIcon
              icon={<FilterOutlined style={{ fontSize: '22px', color: '#fa8c16' }} />}
              message={<strong style={{ fontSize: '15px' }}>PLEASE SELECT ACADEMIC YEAR, CLUSTER &amp; STANDARD FIRST</strong>}
              description={
                <div style={{ marginTop: '8px', fontSize: '13px', lineHeight: '1.6' }}>
                  <p style={{ marginBottom: '8px' }}>
                    Subjects Setup &amp; Marks Configuration is specific to each combination of Academic Year, Cluster, and Standard.
                    Please select Academic Year, Cluster, and Standard from the top filter bar to setup subjects or upload candidates (Institution selection is optional; if omitted, candidates will be mapped dynamically based on the <code>CODE</code> column in your CSV file):
                  </p>
                  <Row gutter={[12, 8]}>
                    <Col xs={24} sm={12} md={8}>
                      Academic Year: {selectedYearId ? <Tag color="green">{selectedYearName}</Tag> : <Tag color="red">Not Selected</Tag>}
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                      Cluster: {selectedClusterId ? <Tag color="green">{selectedClusterName}</Tag> : <Tag color="red">Not Selected</Tag>}
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                      Standard: {selectedStandardId ? <Tag color="green">{selectedStandardName}</Tag> : <Tag color="red">Not Selected</Tag>}
                    </Col>
                  </Row>
                </div>
              }
              style={{ margin: '16px 0', padding: '16px 24px' }}
            />
          ) : (
            <div>
              {/* ── Section 1: Subject Setup & Result Thresholds ── */}
              <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                {/* Subjects Setup Card */}
                <Col xs={24} lg={12}>
                  <Card
                    title={
                      <Space>
                        <BookOutlined style={{ color: '#52c41a' }} />
                        <span>SUBJECTS SETUP &amp; MARKS CONFIG</span>
                        <Badge count={`${subjectConfigs.length} Subjects`} style={{ backgroundColor: '#52c41a' }} />
                      </Space>
                    }
                    extra={
                      <Space wrap>
                        <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={handleAddSubjectField}>
                          Add Subject
                        </Button>
                        <Button
                          size="small"
                          type="primary"
                          icon={<SaveOutlined />}
                          loading={savingSubjects}
                          onClick={handleSaveSubjectConfigs}
                        >
                          {hasExistingConfigs ? 'Update Setup' : 'Save Setup'}
                        </Button>
                      </Space>
                    }
                    bordered
                    style={{ height: '100%' }}
                  >
                    <div style={{ maxHeight: '180px', overflowY: 'auto', paddingRight: '4px' }}>
                      <Row gutter={[8, 8]} style={{ fontWeight: 'bold', marginBottom: 6, textAlign: 'center' }}>
                        <Col span={10}>SUBJECT NAME</Col>
                        <Col span={6}>MAX MARK</Col>
                        <Col span={6}>MINI MARK</Col>
                        <Col span={2}></Col>
                      </Row>

                      {subjectConfigs.map((sub, index) => (
                        <Row gutter={[8, 8]} key={index} style={{ marginBottom: 6 }} align="middle">
                          <Col span={10}>
                            <Input
                              size="small"
                              placeholder="Subject Name"
                              value={sub.subject_name}
                              onChange={(e) => handleSubjectChange(index, 'subject_name', e.target.value)}
                            />
                          </Col>
                          <Col span={6}>
                            <InputNumber
                              size="small"
                              style={{ width: '100%' }}
                              min={1}
                              max={1000}
                              value={sub.max_mark}
                              onChange={(val) => handleSubjectChange(index, 'max_mark', val)}
                            />
                          </Col>
                          <Col span={6}>
                            <InputNumber
                              size="small"
                              style={{ width: '100%' }}
                              min={0}
                              max={sub.max_mark || 1000}
                              value={sub.min_mark}
                              onChange={(val) => handleSubjectChange(index, 'min_mark', val)}
                            />
                          </Col>
                          <Col span={2} style={{ textAlign: 'center' }}>
                            <Button
                              type="text"
                              danger
                              size="small"
                              icon={<DeleteOutlined />}
                              onClick={() => handleRemoveSubjectField(index)}
                            />
                          </Col>
                        </Row>
                      ))}
                    </div>
                  </Card>
                </Col>

                {/* Result Classification Thresholds Card */}
                <Col xs={24} lg={12}>
                  <Card
                    title={
                      <Space>
                        <CheckCircleOutlined style={{ color: '#fa8c16' }} />
                        <span>RESULT CLASSIFICATION THRESHOLDS</span>
                      </Space>
                    }
                    extra={
                      <Space>
                        <Button
                          size="small"
                          type="primary"
                          icon={<SaveOutlined />}
                          loading={savingResultCfg}
                          onClick={handleSaveResultConfig}
                        >
                          Save Result Rules
                        </Button>
                      </Space>
                    }
                    bordered
                    style={{ height: '100%' }}
                  >
                    <Row gutter={[12, 12]} align="middle">
                      <Col xs={12} sm={12}>
                        <div style={{ fontWeight: 600, color: '#ff4d4f', marginBottom: 4 }}>&lt;= FAIL</div>
                        <InputNumber
                          size="small"
                          addonAfter="%"
                          style={{ width: '100%' }}
                          value={resultConfig.fail_max_pct}
                          onChange={(val) => setResultConfig({ ...resultConfig, fail_max_pct: val })}
                        />
                      </Col>
                      <Col xs={12} sm={12}>
                        <div style={{ fontWeight: 600, color: '#52c41a', marginBottom: 4 }}>&lt;= PASS</div>
                        <InputNumber
                          size="small"
                          addonAfter="%"
                          style={{ width: '100%' }}
                          value={resultConfig.pass_max_pct}
                          onChange={(val) => setResultConfig({ ...resultConfig, pass_max_pct: val })}
                        />
                      </Col>
                      <Col xs={12} sm={8}>
                        <div style={{ fontWeight: 600, color: '#fa8c16', marginBottom: 4 }}>&lt;= SECOND CLASS</div>
                        <InputNumber
                          size="small"
                          addonAfter="%"
                          style={{ width: '100%' }}
                          value={resultConfig.second_class_max_pct}
                          onChange={(val) => setResultConfig({ ...resultConfig, second_class_max_pct: val })}
                        />
                      </Col>
                      <Col xs={12} sm={8}>
                        <div style={{ fontWeight: 600, color: '#1890ff', marginBottom: 4 }}>&lt;= FIRST CLASS</div>
                        <InputNumber
                          size="small"
                          addonAfter="%"
                          style={{ width: '100%' }}
                          value={resultConfig.first_class_max_pct}
                          onChange={(val) => setResultConfig({ ...resultConfig, first_class_max_pct: val })}
                        />
                      </Col>
                      <Col xs={12} sm={8}>
                        <div style={{ fontWeight: 600, color: '#722ed1', marginBottom: 4 }}>&lt;= DISTINCTION</div>
                        <InputNumber
                          size="small"
                          addonAfter="%"
                          style={{ width: '100%' }}
                          value={resultConfig.distinction_max_pct}
                          onChange={(val) => setResultConfig({ ...resultConfig, distinction_max_pct: val })}
                        />
                      </Col>
                    </Row>
                  </Card>
                </Col>
              </Row>

              {/* ── Section 2: CSV Upload & Manual Entry ── */}
              <Card
                title={
                  <Space>
                    <CloudUploadOutlined style={{ color: '#1890ff' }} />
                    <span>CANDIDATE MARKS UPLOAD &amp; ENTRY</span>
                  </Space>
                }
                bordered
              >
                <Alert
                  type="info"
                  showIcon
                  message={
                    <span>
                      <strong>Selected Context: </strong>
                      Academic Year: <strong>{selectedYearName}</strong> | Cluster: <strong>{selectedClusterName}</strong> | Institution: <strong>{selectedInstitutionName}</strong> | Standard: <strong>{selectedStandardName}</strong>
                    </span>
                  }
                  description="Upload candidate marks CSV for the selected metadata context above, or add candidates individually."
                  style={{ marginBottom: 16 }}
                />

                <Row gutter={[16, 16]}>
                  <Col xs={24} md={14}>
                    <div style={{ padding: '24px', border: '2px dashed #1890ff', borderRadius: '8px', textAlign: 'center', background: '#f0f5ff', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                      <CloudUploadOutlined style={{ fontSize: '44px', color: '#1890ff', marginBottom: '12px' }} />
                      <h3 style={{ margin: '0 0 8px 0', color: '#1890ff' }}>Upload Candidate CSV File</h3>
                      <p style={{ color: '#666', marginBottom: '16px', maxWidth: '420px' }}>
                        {selectedInstitutionId ? (
                          <span>
                            Import candidates and subject marks in bulk for <strong>{selectedClusterName}</strong> - <strong>{selectedInstitutionName}</strong>.
                          </span>
                        ) : (
                          <span>
                            Import candidates and subject marks in bulk for <strong>{selectedClusterName}</strong>. Candidates will be automatically mapped to institutions based on the <strong>CODE</strong> column in your CSV.
                          </span>
                        )}
                      </p>
                      <Space wrap justify="center">
                        <Upload beforeUpload={handleCsvFileUpload} showUploadList={false} accept=".csv">
                          <Button type="primary" icon={<UploadOutlined />} size="large">
                            Select &amp; Upload CSV
                          </Button>
                        </Upload>
                        <Button icon={<DownloadOutlined />} size="large" onClick={handleDownloadSampleTemplate}>
                          Download Sample Template
                        </Button>
                      </Space>
                    </div>
                  </Col>

                  <Col xs={24} md={10}>
                    <div style={{ padding: '20px', border: '1px solid #e8e8e8', borderRadius: '8px', background: '#fafafa', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                      <div>
                        <h4 style={{ margin: '0 0 12px 0', color: '#1890ff' }}>CSV Upload Instructions</h4>
                        <ul style={{ paddingLeft: '20px', color: '#555', margin: 0, lineHeight: '1.8', fontSize: '13px' }}>
                          <li>Make sure Subject Config &amp; Max/Min marks are saved above.</li>
                          <li>CSV header must contain candidate <code>name</code> and configured subject names.</li>
                          {!selectedInstitutionId && (
                            <li style={{ color: '#d4380d', fontWeight: 'bold' }}>
                              CSV must contain a <code>CODE</code> (or <code>School Code</code>) column to match candidates to institutions.
                            </li>
                          )}
                          <li>Optional CSV headers: <code>gender</code>, <code>parent_name</code>, <code>contact</code>, <code>email</code>, <code>dob</code>, <code>combination</code>, <code>mode</code>{selectedInstitutionId && `, code`}.</li>
                        </ul>
                      </div>
                      <Divider style={{ margin: '12px 0' }} />
                      <Button type="dashed" block icon={<PlusOutlined />} onClick={handleOpenAddCandidateModal}>
                        Add Single Candidate Manually
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Card>
            </div>
          )}
        </div>
      )
    }
  ];

  return (
    <div style={{ padding: '16px' }}>
      {/* ── Main Tabbed View: Tab 1 (View Uploaded Data), Tab 2 (Upload & Setup) ── */}
      <Tabs
        activeKey={activeTabKey}
        onChange={(key) => setActiveTabKey(key)}
        type="card"
        size="large"
        items={tabItems}
      />

      {/* ── Candidate Add/Edit Modal ── */}
      <Modal
        title={editingCandidate ? 'Edit Candidate Details' : 'Add New Candidate'}
        open={isCandModalOpen}
        onCancel={() => setIsCandModalOpen(false)}
        onOk={() => candForm.submit()}
        width={750}
        destroyOnClose
      >
        <Form form={candForm} layout="vertical" onFinish={handleSaveCandidate}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="CANDIDATE NAME"
                rules={[{ required: true, message: 'Candidate Name is required!' }]}
              >
                <Input placeholder="Enter Candidate Full Name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="parent_name" label="PARENT / GUARDIAN NAME">
                <Input placeholder="Enter Parent or Guardian Name" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="gender" label="GENDER">
                <Select showSearch optionFilterProp="children" placeholder="Select Gender">
                  <Option value="Male">Male</Option>
                  <Option value="Female">Female</Option>
                  <Option value="Other">Other</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="contact" label="CONTACT">
                <Input placeholder="Phone Number" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="dob" label="DATE OF BIRTH">
                <Input placeholder="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="email" label="E-MAIL">
                <Input placeholder="Enter Email Address" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="combination" label="COMBINATION">
                <Input placeholder="Enter Combination (e.g. HEBA, PCMB)" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="mode" label="MODE">
                <Input placeholder="Enter Mode (e.g. Regular, Private)" />
              </Form.Item>
            </Col>
            {(!selectedClusterInstitutionId && !selectedInstitutionId) && (
              <Col span={12}>
                <Form.Item 
                  name="cluster_institution_id" 
                  label="INSTITUTION"
                  rules={[{ required: true, message: 'Please select an Institution!' }]}
                >
                  <Select showSearch optionFilterProp="children" placeholder="Select Institution">
                    {institutions.map((i) => {
                      const instCode = i.cluster_institution_code || i.institution_code || i.code || '';
                      const label = instCode ? `${instCode} - ${i.institution_name}` : i.institution_name;
                      const optVal = i.cluster_institution_id || i.institution_id;
                      return (
                        <Option key={optVal} value={optVal}>
                          {label}
                        </Option>
                      );
                    })}
                  </Select>
                </Form.Item>
              </Col>
            )}
          </Row>

          <Divider children="FINAL EXAM SUBJECT MARKS" />

          <Row gutter={[16, 16]}>
            {displaySubjects.map((sub) => (
              <Col span={12} key={sub.subject_name}>
                <Form.Item
                  name={['marks', sub.subject_name]}
                  label={
                    <Space size={4} wrap>
                      <strong>{sub.subject_name}</strong>
                      <Tag color="red">Min Pass: {Math.round(sub.min_mark || 30)}</Tag>
                      <Tag color="blue">Max: {Math.round(sub.max_mark || 100)}</Tag>
                    </Space>
                  }
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    max={Math.round(sub.max_mark || 1000)}
                    placeholder={`Enter marks (Min pass: ${Math.round(sub.min_mark || 30)})`}
                  />
                </Form.Item>
              </Col>
            ))}
          </Row>

          <Divider children="RESULT SELECTION &amp; MANUAL OVERRIDE" />

          <Form.Item
            name="override_result"
            label="FINAL RESULT EVALUATION RULE"
            extra="Select 'Auto-Calculate' to let the system evaluate based on subject pass marks & percentages, or choose an explicit result (e.g. PASS for 0 marks, FAIL for 35 marks)."
          >
            <Select showSearch optionFilterProp="children" style={{ width: '100%' }} placeholder="Auto-Calculate (Default)">
              <Option value="AUTO">Auto-Calculate (Default Rule based on Marks)</Option>
              <Option value="PASS">PASS (Manual Override - e.g. 0 Marks Passed)</Option>
              <Option value="FAIL">FAIL (Manual Override - e.g. 35 Marks Failed)</Option>
              <Option value="SECOND CLASS">SECOND CLASS (Manual Override)</Option>
              <Option value="FIRST CLASS">FIRST CLASS (Manual Override)</Option>
              <Option value="DISTINCTION">DISTINCTION (Manual Override)</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* ── CSV Import Preview Modal ── */}
      <Modal
        title="Preview CSV Candidates Import"
        open={isCsvModalOpen}
        onCancel={() => setIsCsvModalOpen(false)}
        onOk={handleConfirmCsvUpload}
        confirmLoading={uploadingCsv}
        width={1100}
        okText="Import Candidates"
        style={{ top: 20 }}
      >
        <p>Found <strong>{csvParsedData.length}</strong> candidates in the uploaded file.</p>
        <Table
          dataSource={csvParsedData}
          rowKey={(rec, index) => index}
          size="small"
          bordered
          pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: ['10', '25', '50', '100', '200', '500', '1000'] }}
          scroll={{ x: 'max-content' }}
          columns={[
            { title: 'CODE', dataIndex: 'code', render: (val) => val || '-' },
            { title: 'NAME', dataIndex: 'name' },
            { title: 'GENDER', dataIndex: 'gender', render: (val) => val || '-' },
            { title: 'PARENT / GUARDIAN', dataIndex: 'parent_name', render: (val) => val || '-' },
            { title: 'CONTACT', dataIndex: 'contact', render: (val) => val || '-' },
            { title: 'COMBINATION', dataIndex: 'combination', render: (val) => val || '-' },
            { title: 'MODE', dataIndex: 'mode', render: (val) => val || '-' },
            {
              title: 'MARKS SUMMARY',
              render: (_, rec) => (
                <span>
                  {Object.entries(rec.marks || {})
                    .map(([k, v]) => `${k}: ${Math.round(v)}`)
                    .join(' | ')}
                </span>
              )
            },
            {
              title: 'RESULT RULE',
              dataIndex: 'override_result',
              render: (val) => val && val !== 'AUTO' ? <Tag color={val === 'FAIL' ? 'volcano' : 'green'}>{val}</Tag> : <Tag color="blue">AUTO</Tag>
            }
          ]}
        />
      </Modal>
    </div>
  );
};

export default CandidateManagementPage;
